import React, { useEffect, useRef, useState } from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import { TranslateFunction } from 'react-localize-redux';

import { Typography } from '../../../../components/atoms/Typography';
import {
  OrderCollection as CollectionIcon,
  OrderDelivery as DeliveryIcon,
} from '../../../../components/icons';
import { InlineBulletList } from '../../../../components/molecules';
import { OrderStatus } from '../../../../components/partials';
import { flagService } from '../../../../core/services';
import {
  FulfilmentStatus,
  OrderHistoryData,
  OrderHistoryItems,
} from '../../../../core/state/order/order.types';
import { getFulfilmentStatus } from '../../../../core/state/order/orderMiddleware';
import {
  getDateFromMsDate,
  getRelativeDate,
} from '../../../../utils/dateTimeUtils';
import * as Api from '../../../../weborder/Api';
import * as BasketControl from '../../../../weborder/BasketControl';
import { orderHistoryUtils } from '../../utils';

type Props = {
  currentLang: string;
  orderedItem: OrderHistoryData;
  translate: TranslateFunction;
};

const useStyles = makeStyles((theme: Theme) => ({
  itemString: {
    marginBottom: theme.spacing(0.25),
  },

  orderDetails: {
    alignItems: 'center',
    display: 'flex',
    flexFlow: 'row nowrap',
    marginBottom: theme.spacing(1),

    '& .fd-icon': {
      marginRight: theme.spacing(1),
    },
  },

  orderInfo: {
    paddingRight: theme.spacing(3),
    width: '100%',

    '& .fd-text': {
      display: '-webkit-box',
      overflow: 'hidden',
      wordBreak: 'break-word',
      '-webkit-line-clamp': 2,
      '-webkit-box-orient': 'vertical',
    },
  },

  orderStatus: {
    padding: 0,
    width: '100%',
  },

  [theme.breakpoints.up('md')]: {
    orderStatus: {
      paddingRight: theme.spacing(2),
    },
  },
}));

export function OrderHistoryItem(props: Props): JSX.Element {
  const { currentLang, orderedItem, translate } = props;
  const classes = useStyles();
  const [orderStatus, setOrderStatus] = useState<FulfilmentStatus>(
    orderedItem.FulfillmentStatus,
  );

  const isOrderStatusEnabled = flagService.isFlagOn('orderStatus');

  const isOrderInProgress =
    isOrderStatusEnabled &&
    orderStatus &&
    !orderStatus.IsError &&
    orderStatus.StatusPosition < orderStatus.TotalStatuses;

  const loadOrderStatus = (): Promise<void> =>
    getFulfilmentStatus(orderedItem.OrderId)
      .then((status) => setOrderStatus(status))
      .catch(() => {
        setOrderStatus(null);
      });

  const handleVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      statusTimer.current = setTimeout(() => {
        loadOrderStatus();
      }, 500);
    }
  };
  const statusTimer = useRef<NodeJS.Timeout>();
  useEffect(() => {
    if (isOrderInProgress) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (statusTimer.current) {
        clearTimeout(statusTimer.current);
      }
    };
  }, [isOrderInProgress]);

  const renderOrderDetails = (
    customerAddress: string,
    deliveryType: Api.DeliveryType,
    restaurantName: string,
  ): JSX.Element => {
    const isCollection = deliveryType === Api.DeliveryType.Collection;

    const icon = isCollection ? (
      <CollectionIcon size="xs" />
    ) : (
      <DeliveryIcon size="xs" />
    );

    const orderTypeString = isCollection
      ? translate('HistoryPage_Collection_information')
      : translate('HistoryPage_Delivery_information');
    const orderDestination = isCollection ? restaurantName : customerAddress;

    let orderTypeDetailsString = orderDestination
      ? `${orderTypeString} - ${orderDestination}`
      : orderTypeString;

    if (isOrderInProgress && !isCollection && orderDestination) {
      orderTypeDetailsString = translate('Delivering_to_order', {
        address: orderDestination,
      }) as string;
    }

    return (
      <div className={classes.orderDetails}>
        {icon}
        <Typography variant="caption">{orderTypeDetailsString}</Typography>
      </div>
    );
  };

  const renderOrderedItems = (
    orderedItems: OrderHistoryItems[],
  ): JSX.Element => {
    if (!orderedItems) return null;

    const orderedItemsArray = [];
    orderedItems.forEach((item) => orderedItemsArray.push(item.Name));

    const countItemAppearance = {};
    orderedItemsArray.forEach((item) => {
      if (countItemAppearance[item]) {
        countItemAppearance[item] += 1;
        return;
      }
      countItemAppearance[item] = 1;
    });

    const arrayOfItems = Object.entries(countItemAppearance).reduce(
      (acc, [name, quantity]) => {
        quantity === 1 ? acc.push(name) : acc.push(`${quantity}x ${name}`);
        return acc;
      },
      [],
    );

    const listOfOrderedItems = arrayOfItems.join(', ');

    return (
      <Typography className={classes.itemString}>
        <em>{listOfOrderedItems}</em>
      </Typography>
    );
  };

  const renderPriceAndTime = (): JSX.Element => {
    const notes = [];
    const price = BasketControl.ToCurrency(
      orderedItem.TotalAmount,
      orderedItem.Currency,
      currentLang,
    );

    const getPaymentMethodLabel = (): string => {
      let paymentString = '';
      if (!orderedItem.PaymentAccountDescription) {
        return paymentString;
      }

      paymentString = orderHistoryUtils.localizePaymentMethodLabel(
        orderedItem.PaymentAccountDescription,
        translate,
      );
      return paymentString;
    };

    const priceAndPaymentString = `${price} ${getPaymentMethodLabel()}`;
    notes.push(priceAndPaymentString);

    const orderTime = getDateFromMsDate(orderedItem.TsOrderPlaced);
    const relativeOrderDate = getRelativeDate(
      orderTime,
      translate,
      currentLang,
    );
    notes.push(relativeOrderDate);

    return <InlineBulletList notes={notes} />;
  };

  return (
    <div
      className={classes.orderInfo}
      key={`ordered-item-${orderedItem.OrderId}`}
    >
      {renderOrderDetails(
        orderedItem.DeliveryLocationAddressString,
        orderedItem.DeliveryType,
        orderedItem.RestaurantName,
      )}
      {renderOrderedItems(orderedItem.OrderHistoryItems)}
      {renderPriceAndTime()}
      {isOrderInProgress && (
        <OrderStatus
          className={classes.orderStatus}
          orderStatus={orderStatus}
          translate={translate}
          variant="subHeader"
        />
      )}
    </div>
  );
}
